import {Alert, Button, Collapse, Form, Input, Menu, Row, Tooltip} from 'antd';
import {ErrorMessage, Formik} from 'formik';
import React, {useRef, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {EyeOutlined, InfoCircleOutlined} from '@ant-design/icons';

import {Client, Country, User} from '../../../../types/src';
import {AdminFeatureIcon, brandConfig, FormRow} from '../../../../ui/src';
import {InfoIcon} from '../';
import {STRINGS} from '../..';
import {SwitchRow} from '../campaignDetails/common';
import {BaseModal} from '../BaseModal';
import {ConfirmationModal} from '../confirmationModal';
import {ProxySettings} from '../ProxySettings';
import {UserRequirementsPopover} from './userRequirementsPopover';
import {UserRequirementsTooltip} from './userRequirementsTooltip';
import {TermsAndConditions} from './TermsAndConditions';
import {UserActionDropdown} from './UserActionDropdown';

import './styles.scss';

interface UserEditModalProps {
  user: User;
  users: User[];
  client: Client;
  members: any;
  visible: boolean;
  loading: boolean;
  error: string;
  onSave: (user: User) => Promise<User>;
  handleCancel: () => void;
  cleanup: () => void;
  archiveUser: (id: string | number) => void;
  unarchiveUser: (id: string | number) => void;
  isAdmin: boolean;
  removeProxy: (id: number) => void;
  getProxyLocations: () => Promise<Country[]>;
  locationsInfo: any;
  suggestedProxy: any;
}

export const ShortNewUserModal = ({
  onSave,
  user,
  cleanup,
  handleCancel,
  users,
  client,
  members,
  archiveUser,
  unarchiveUser,
  visible,
  loading,
  error,
  isAdmin,
  removeProxy,
  getProxyLocations,
  locationsInfo,
  suggestedProxy,
}: UserEditModalProps) => {
  const formikRef = useRef(null);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [isCustomProxy, setIsCustomProxy] = useState(false);
  const isFirstUserForClient = users.length === 0;
  const handleValidate = (values: any) => {
    const errors: any = {};
    if (!values.email) {
      errors.email = 'Required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Must be an email';
    }
    if (isCustomProxy) {
      if (!values.custom_proxy_ip) {
        errors.custom_proxy_ip = 'Required';
      }
      if (!values.custom_proxy_password) {
        errors.custom_proxy_password = 'Required';
      }
      if (!values.custom_proxy_port) {
        errors.custom_proxy_port = 'Required';
      }
      if (!values.custom_proxy_username) {
        errors.custom_proxy_username = 'Required';
      }
    }

    return errors;
  };

  const onClose = () => {
    if (formikRef.current?.dirty) {
      setConfirmationVisible(true);
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    setConfirmationVisible(false);
    cleanup();
    handleCancel();
  };

  const isLockCreateTeamMember = email => {
    return client.email === email || users.some(u => u.email === email) || members.some(m => m.email === email);
  };

  const handleChangeEmail = (value, setFieldValue) => {
    setFieldValue('email', value);
    setFieldValue('create_team_member', !isLockCreateTeamMember(value));
  };

  const handleAction = async ({key}: any) => {
    if (key === 'archive') {
      archiveUser(user.id);
    } else if (key === 'unarchive') {
      unarchiveUser(user.id);
    }
  };

  const getMenu = () => {
    return (
      <UserActionDropdown onClick={handleAction}>
        {!user.archived && (
          <Menu.Item key="archive">
            Archive <InfoIcon message={STRINGS.tooltip_archive_user} />
          </Menu.Item>
        )}
        {!!user.archived && (
          <Menu.Item key="unarchive">
            <AdminFeatureIcon /> Unarchive
          </Menu.Item>
        )}
      </UserActionDropdown>
    );
  };

  return (
    <BaseModal
      visible={visible}
      loading={loading}
      onClose={onClose}
      title={
        <div className="userEditModalTitle">
          <span>User Details</span>
          <span>{user.id ? getMenu() : null}</span>
        </div>
      }
      className="userEditModal"
    >
      <Formik
        innerRef={formikRef}
        initialValues={{
          id: user.id,
          email: user.email,
          create_team_member: user.create_team_member,
        }}
        enableReinitialize={true}
        validate={handleValidate}
        onSubmit={values => onSave({...user, ...values})}
      >
        {({values, errors, touched, dirty, handleBlur, handleSubmit, setFieldValue}) => (
          <Form onFinish={handleSubmit} autoComplete="off" layout="vertical">
            <div className="userEditModalFormContainer">
              {!!user.archived && (
                <div className="viewOnlyOverlay">
                  <span className="viewOnlyOverlayText">
                    <EyeOutlined /> View only
                  </span>
                </div>
              )}
              <FormRow>
                <Form.Item
                  label={
                    <>
                      <b>Email: </b>
                      <Tooltip title="Please enter your correct LinkedIn login email.">
                        <InfoCircleOutlined className="tooltip-icon userEditModalTooltipIconMargin" />
                      </Tooltip>
                    </>
                  }
                >
                  <Input
                    value={values.email}
                    name="email"
                    className={errors.email && touched.email ? 'invalid-input' : ''}
                    autoComplete="client-user-email"
                    onChange={e => handleChangeEmail(e.target.value, setFieldValue)}
                    onBlur={handleBlur}
                    placeholder="Type your user linkedin email used for logging in on LinkedIn"
                  />
                  <ErrorMessage name="email" className="error-message" component="div" />
                </Form.Item>
              </FormRow>
              {client.proxy_feature && (
                <FormRow>
                  <ProxySettings
                    client={client}
                    proxy={user.proxy}
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    isAdmin={isAdmin}
                    isCustomProxy={isCustomProxy}
                    setIsCustomProxy={setIsCustomProxy}
                    removeProxy={removeProxy}
                    getProxyLocations={getProxyLocations}
                    locationsInfo={locationsInfo}
                    suggestedProxy={suggestedProxy}
                  />
                </FormRow>
              )}

              <Collapse bordered={false} className="userEditModalCollapse">
                <Collapse.Panel header="Advanced options" className="userEditModalCollapsePanel" key="1">
                  {!user.id && (
                    <FormRow>
                      <SwitchRow
                        checked={values.create_team_member}
                        label={
                          <span>
                            Send invite to access {brandConfig.name}:{' '}
                            <InfoIcon
                              // filled={false}
                              className="tooltip-icon"
                              message={STRINGS.tooltip.create_team_member}
                            />
                          </span>
                        }
                        onChange={(checked: boolean) => setFieldValue('create_team_member', checked)}
                        isLock={isLockCreateTeamMember(values.email)}
                      />
                    </FormRow>
                  )}
                </Collapse.Panel>
              </Collapse>
            </div>

            <div className="userEditModalFormContent">
              {isFirstUserForClient && (
                <Alert
                  message={
                    <span>
                      {`Please read `}
                      <b>
                        {isMobile ? (
                          <UserRequirementsPopover>
                            <Button type="link" className="userRequirementsButtonLink">
                              LinkedIn User Recommendations
                            </Button>
                          </UserRequirementsPopover>
                        ) : (
                          <UserRequirementsTooltip>
                            <Button type="link" className="userRequirementsButtonLink">
                              LinkedIn User Recommendations
                            </Button>
                          </UserRequirementsTooltip>
                        )}
                      </b>
                    </span>
                  }
                  type="info"
                  className="userEditModalFormRowWithAlert"
                />
              )}
              {error && (
                <Row className="userEditModalFormRowWithMarginBottom">
                  <Alert message={error} type="error" />
                </Row>
              )}
              <div className="userEditModalButtons">
                <div>
                  <TermsAndConditions />
                </div>
                <div>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button
                    className="userEditModalSubmitButton"
                    loading={loading}
                    disabled={!!user.archived}
                    type="primary"
                    htmlType="submit"
                  >
                    Save and Validate
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <ConfirmationModal
        visible={confirmationVisible}
        onOk={closeModal}
        onCancel={() => setConfirmationVisible(false)}
      />
    </BaseModal>
  );
};
